import React, { useState } from 'react';
import { CountDown } from '../../CountDown/CountDown';
import { GameGraphics } from '../../GameGraphics/GameGraphics';
import { Header } from '../../Header/Header';
import { Link } from '../../Link/Link';
import { Logo } from '../../Logo/Logo';
import { Paragraph } from '../../Paragraph/Paragraph';
import { GameActions } from '../../TileActions/GameActions';
import { TilePill } from '../../TilePill/TilePill';
import type { TileProps } from '../tiles';
import { GAME_UNAVAILABLE } from '../../../helpers/tileHelper';
import { track } from '../../../helpers/tracking';
import { isMobile, isTablet } from '@SharedScripts/Utils/DeviceClass';
import { LargeTileWrapper } from '../TileWrappers';
import BlurGradientBackground from '../../BlurGradientBackground/BlurGradientBackground';

export const LargeTile = ({
  title,
  type,
  bannerInfo,
  gameGraphics,
  minWager,
  favourited,
  isLoading,
  navigationUrl,
  navigationUrlWithLaunchParam,
  potSize,
  playersText,
  dealerName,
  liveStreamLink,
  openFrom,
  isTableClosed,
  isUnavailable,
  primaryButtonCallback,
  tabIndex,
  className,
}: TileProps) => {
  const [graphicsLoading, setGraphicsLoading] = useState(true);
  const backgroundUrl = type === 'Live' ? gameGraphics?.backgroundUrl : gameGraphics?.landscapeUrl;
  const [graphicsError, setGraphicsError] = useState(false);

  const linkAriaLabel = `Start spil ${title}`;

  const graphicsDisabled =
        isUnavailable || (type !== 'Casino' && isTableClosed);

  const gradientErrorState =
        !backgroundUrl ||
        isLoading ||
        graphicsDisabled ||
        graphicsError;

  const shouldRenderLogo = (graphicsError && !graphicsDisabled) || !backgroundUrl;

  return (
    <LargeTileWrapper className={className}>
      <div data-tracking-meta-data={JSON.stringify({
        action: isMobile() || isTablet() ? 'click game tile' : 'game open',
        label: isMobile() || isTablet() ? 'mobile' : 'desktop',
      })}
      onClick={track}
      >
        <Link
          className='pointer-events-auto absolute top-0 right-0 bottom-0 left-0 z-10 rounded-32 focus-visible:ring-primary'
          href={navigationUrl}
          ariaLabel={linkAriaLabel}
          tabIndex={tabIndex}
          disabled={isUnavailable || !navigationUrl}
        />
      </div>

      <GameGraphics
        {...gameGraphics}
        backgroundUrl={backgroundUrl}
        foregroundUrl={type === 'Live' ? gameGraphics?.foregroundUrl : ''}
        isLoading={graphicsLoading}
        onLoaded={() => setGraphicsLoading(false)}
        onError={() => setGraphicsError(true)}
        error={graphicsError}
        imageWidth={400}
        lazyLoad={true}
        disabled={graphicsDisabled}
        liveStreamLink={liveStreamLink}
        disableCache={type === 'Live'}
        useDarkGradient={type === 'Live' && !isUnavailable}
        rounded='rounded-32'
        backgroundStyle={`${liveStreamLink ? 'h-full left-1/2 -translate-x-1/2' : 'object-top'}`}
      />

      <div
        className='flex h-full flex-grow flex-col p-16 pb-32 text-neutrals-white fill-neutrals-white stroke-neutrals-white'
      >
        <div className='relative flex w-full min-h-24'>
          {potSize && (
            <TilePill
              text={potSize}
              icon='jackpot'
              background={isUnavailable ? 'grey' : 'purple'}
              className={bannerInfo ? 'ml-16' : ''}
            />
          )}
          {minWager && (
            <TilePill
              text={minWager}
              icon='bid'
              background={isUnavailable ? 'grey' : 'dark-grey'}
              className='ml-auto'
            />
          )}
        </div>

        <div className='z-10 m-auto h-full'>


          {shouldRenderLogo && (
            <Logo
              className={'m-auto items-center w-56 -mt-24'} />
          )}

          {isUnavailable && (
            <Paragraph
              size={14}
              weight='bold'
              className='relative my-auto self-center text-center max-w-[20ch] text-neutrals-grey-950'
            >
              {GAME_UNAVAILABLE}
            </Paragraph>
          )}

          {isTableClosed && !isUnavailable && (
            <CountDown
              className='relative my-auto self-center text-center text-white text-20'
              openingTime={openFrom ?? ''}
            />
          )}
        </div>
      </div>

      <div
        className={`absolute bottom-0 left-0 w-full h-auto min-h-96 rounded-b-32 top ${gradientErrorState ? 'bg-fallback-gradient' : 'bg-primary-gradient'}`}
      >
        <BlurGradientBackground/>

        <div className='absolute bottom-0 flex w-full flex-col pt-12 pb-32 text-neutrals-white fill-neutrals-white'>
          <Header
            as='h3'
            size='lg'
            className='mt-auto text-center font-extrabold px-16'
            italic={true}
            truncate={2}
          >
            {title ?? ''}
          </Header>
          {!isTableClosed && !isUnavailable && type === 'Live' && (
            <div className='relative flex self-center'>
              {dealerName && <TilePill
                text={dealerName}
                icon='dealers'
                className='!backdrop-blur-none'
              />}
              {playersText && <TilePill
                text={playersText}
                icon='players'
                className='!backdrop-blur-none'
              />}
            </div>
          )}
        </div>

      </div>

      {!isUnavailable && (
        <GameActions
          href={navigationUrlWithLaunchParam}
          linkAriaLabel={linkAriaLabel}
          favourited={favourited}
          favouritedAriaLabel={`Føj ${title} til favouritter`}
          primaryButtonCallback={primaryButtonCallback}
          tabIndex={tabIndex}
          isTableClosed={isTableClosed}
          className='absolute bottom-0 self-center translate-y-1/2 is-desktop-detected:opacity-0 is-desktop-detected:group-hover:opacity-100 z-20 gap-12
          is-desktop-detected:group-focus-within:opacity-100 is-desktop-detected:group-focus:opacity-100 transition-opacity duration-100 ease-in-out'
        />
      )}
    </LargeTileWrapper>
  );
};
